import React, { FC, useMemo } from "react";
import { Alert } from "antd";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app";
import { DocumentSetFetchingErrorType } from "../../interfaces/documentSet";
import selectUserInputIds from "../../../user/selectors/userInputIdsSelector";

const FetchingErrorAlert: FC = () => {
  const { t } = useTranslation("overview", {
    keyPrefix: "fetchingErrorType",
  });

  const { fetchingErrorType, hasColumnItemsFetchingError, loading } =
    useAppSelector((state) => state.documentSetsReducer);

  const {
    user,
    isTokenRefreshing,
    isLoading: isUserLoading,
  } = useAppSelector((state) => state.userReducer);
  const { overviewMap } = useAppSelector((state) => state.appReducer);
  const userInputIds = useAppSelector(selectUserInputIds);

  // No user session while nothing is (re)authenticating means auth failed,
  // not that column items couldn't be fetched. Surface that distinctly.
  const hasNoSession = useMemo(() => {
    return !user && !isUserLoading && !isTokenRefreshing;
  }, [user, isUserLoading, isTokenRefreshing]);

  // A logged-in user with input sources but no column items is a genuine fetch
  // problem. Having no sources at all is handled by NoSourcesAlert instead.
  const hasNoColumnItems = useMemo(() => {
    return (
      !!user &&
      !!userInputIds.length &&
      !loading &&
      !overviewMap.length &&
      !isUserLoading
    );
  }, [user, userInputIds.length, loading, overviewMap.length, isUserLoading]);

  const message = useMemo(() => {
    if (
      (!fetchingErrorType &&
        !hasColumnItemsFetchingError &&
        !hasNoColumnItems &&
        !hasNoSession) ||
      isTokenRefreshing
    ) {
      return null;
    }

    if (hasNoSession) {
      return t("sessionError");
    } else if (
      fetchingErrorType === DocumentSetFetchingErrorType.INVALID_QUERY
    ) {
      return t("invalidQuery");
    } else if (fetchingErrorType === DocumentSetFetchingErrorType.OTHER_ERROR) {
      return t("otherError");
    } else if (hasColumnItemsFetchingError || hasNoColumnItems) {
      return t("columnItemsError");
    }

    return null;
  }, [
    fetchingErrorType,
    t,
    hasColumnItemsFetchingError,
    isTokenRefreshing,
    hasNoColumnItems,
    hasNoSession,
  ]);

  if (!message) {
    return null;
  }

  return <Alert message={message} type="error" showIcon />;
};

export default FetchingErrorAlert;
